import React, { useEffect, useState, useCallback } from "react";
import {
  Image,
  Text,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerBody,
  DrawerHeader,
  DrawerCloseButton,
  Flex,
  Box,
  Badge,
  Skeleton,
  IconButton,
  Icon,
  Divider,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
} from "@chakra-ui/react";
import { FaBookOpen, FaMapMarkerAlt, FaPhone, FaGlobe, FaArrowLeft, FaExternalLinkAlt } from "react-icons/fa";
import AWS from "aws-sdk";
import { extractGuideAddress } from "../Logic/guideLocations";
import { bottomDrawerSize } from "./drawerSizing";

// Configure AWS for S3 image fetching
AWS.config.update({
  region: "us-west-2",
  credentials: new AWS.Credentials(
    process.env.REACT_APP_PUBLIC_KEY,
    process.env.REACT_APP_SECRET_KEY
  ),
});

export const GuideDrawer = ({ guide, onClose, isOpen, isInsideModal = false }) => {
  const [renderedDescription, setRenderedDescription] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [imageLoading, setImageLoading] = useState(true);
  const [imageError, setImageError] = useState(false);

  // Fetch and process image URL
  const fetchImageUrl = useCallback(async () => {
    if (!guide?.images?.[0]) {
      setImageLoading(false);
      return;
    }

    const imageKey = guide.images[0];
    
    // If it's already a full URL, use it directly
    if (imageKey.startsWith("http")) {
      setImageUrl(imageKey);
      setImageLoading(false);
      return;
    }

    // Construct optimized CloudFront URL
    const cloudfrontBaseUrl = "https://dl6ri8ttf37m3.cloudfront.net";
    const optimizedUrl = `${cloudfrontBaseUrl}/public/${encodeURIComponent(imageKey)}?width=800&format=webp`;
    
    setImageUrl(optimizedUrl);
    setImageLoading(false);
  }, [guide?.images]);

  useEffect(() => {
    if (isOpen && guide) {
      fetchImageUrl();
      const descriptionHTML = parseDescriptionWithLinksAndEmbeds(guide.description);
      setRenderedDescription(descriptionHTML);
    }
  }, [isOpen, guide, fetchImageUrl]);

  const parseDescriptionWithLinksAndEmbeds = (text) => {
    if (!text) return "";
    
    const urlRegex = /(\b(https?|ftp|file):\/\/[-A-Z0-9+&@#/%?=~_|!:,.;]*[-A-Z0-9+&@#/%=~_|])/gi;
    return text.replace(urlRegex, (url) => {
      // Enhanced regex to include youtu.be links
      const youtubeRegex = /^(https?:\/\/)?(www\.)?(youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]+)/;
      const match = url.match(youtubeRegex);
      if (match) {
        const videoId = match[4];
        // loading="lazy" keeps the embed from booting while the drawer is
        // still sliding in; the browser fetches it when it nears the view.
        return `<iframe width="100%" style="max-width: 100%; padding: 10px 0; border-radius: 8px;" height="200" loading="lazy" src="https://www.youtube.com/embed/${videoId}" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe>`;
      }

      // Return normal link if not a YouTube URL
      return `<a href="${url}" target="_blank" rel="noopener noreferrer" style="color: #817EFF; text-decoration: underline;">${url}</a>`;
    });
  };

  // Extract contact information from description
  const extractContactInfo = (description) => {
    if (!description) return {};
    
    const contactInfo = {};
    
    // Extract phone numbers
    const phoneRegex = /(\+?\d{1,4}[-.\s]?)?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}/g;
    const phones = description.match(phoneRegex);
    if (phones) {
      contactInfo.phone = phones[0];
    }
    
    // Extract websites
    const websiteRegex = /(https?:\/\/[^\s]+)/g;
    const websites = description.match(websiteRegex);
    if (websites) {
      contactInfo.website = websites[0];
    }
    
    // Shared with the hub's map so a guide's pin and its address button can
    // never point at different places.
    const address = extractGuideAddress(description);
    if (address) {
      contactInfo.address = address;
    }
    
    return contactInfo;
  };

  const contactInfo = extractContactInfo(guide?.description);

  // Function to open address in maps
  const openAddressInMaps = (address) => {
    const encodedAddress = encodeURIComponent(address);
    
    if (/iPad|iPhone|iPod/.test(navigator.userAgent)) {
      // Apple Maps
      const appleMapsUrl = `http://maps.apple.com/?q=${encodedAddress}`;
      window.open(appleMapsUrl, '_blank');
    } else {
      // Google Maps
      const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`;
      window.open(googleMapsUrl, '_blank');
    }
  };

  // Function to initiate phone call
  const initiatePhoneCall = (phoneNumber) => {
    const cleanPhone = phoneNumber.replace(/[\s\-\(\)]/g, '');
    const telUrl = `tel:${cleanPhone}`;
    window.open(telUrl, '_self');
  };

  // The body, as plain JSX (not a nested component: defining a component
  // inside render gives it a new identity every pass, which made React
  // unmount and remount the whole body — image reload and all — on every
  // state change while the drawer was opening). Mobile keeps the stacked
  // hero-image-over-text sheet; on md+ it becomes a two-pane dialog with
  // the image pinned on the left and the text content on the right.
  const guideContent = (
    <Flex
      direction={{ base: "column", md: "row" }}
      align={{ md: "flex-start" }}
      px={{ md: 6 }}
      pt={{ md: 6 }}
      gap={{ md: 6 }}
    >
      {/* Image Section — sticky left pane on desktop */}
      {guide?.images?.[0] && (
        <Box
          position={{ base: "relative", md: "sticky" }}
          top={{ md: 0 }}
          w={{ base: "full", md: guide?.description ? "42%" : "full" }}
          h={{ base: "200px", md: "340px" }}
          flexShrink={0}
          overflow="hidden"
          borderRadius={{ md: "16px" }}
        >
          {imageLoading && (
            <Skeleton h={{ base: "200px", md: "340px" }} />
          )}
          <Image
            src={imageUrl}
            alt={guide.title}
            w="full"
            h={{ base: "200px", md: "340px" }}
            objectFit="cover"
            opacity={imageLoading ? 0 : 1}
            transition="opacity 0.3s ease"
            onLoad={() => setImageLoading(false)}
            onError={() => {
              setImageError(true);
              setImageLoading(false);
            }}
          />
        </Box>
      )}

      {/* Content Section — right pane on desktop */}
      <Box px={{ base: 4, md: 0 }} pt={{ base: 4, md: 0 }} flex="1" minW={0}>
        {/* Contact Information */}
        {(contactInfo.phone || contactInfo.website || contactInfo.address) && (
          <Box mb={4} p={3} bg="rgba(255, 255, 255, 0.05)" borderRadius="12px" border="1px solid" borderColor="rgba(255, 255, 255, 0.1)">
            <Text fontSize="12px" fontWeight="600" color="gray.300" mb={2}>
              Contact Information
            </Text>
            
            {contactInfo.address && (
              <Flex alignItems="center" gap={2} mb={2}>
                <Icon as={FaMapMarkerAlt} color="#817EFF" fontSize="12px" />
                <Box
                  as="button"
                  onClick={() => openAddressInMaps(contactInfo.address)}
                  bg="rgba(129, 126, 255, 0.1)"
                  border="1px solid"
                  borderColor="rgba(129, 126, 255, 0.3)"
                  borderRadius="6px"
                  px={2}
                  py={1}
                  display="flex"
                  alignItems="center"
                  gap={1}
                  _hover={{
                    bg: "rgba(129, 126, 255, 0.2)",
                    borderColor: "rgba(129, 126, 255, 0.5)"
                  }}
                  _active={{
                    bg: "rgba(129, 126, 255, 0.3)"
                  }}
                  transition="all 0.2s ease"
                >
                  <Text fontSize="13px" color="white" fontWeight="500" textAlign={'left'}>
                    {contactInfo.address}
                  </Text>
                  <Icon as={FaExternalLinkAlt} color="#817EFF" fontSize="10px" />
                </Box>
              </Flex>
            )}
            
            {contactInfo.phone && (
              <Flex alignItems="center" gap={2} mb={2}>
                <Icon as={FaPhone} color="#817EFF" fontSize="12px" />
                <Box
                  as="button"
                  onClick={() => initiatePhoneCall(contactInfo.phone)}
                  bg="rgba(129, 126, 255, 0.1)"
                  border="1px solid"
                  borderColor="rgba(129, 126, 255, 0.3)"
                  borderRadius="6px"
                  px={2}
                  py={1}
                  display="flex"
                  alignItems="center"
                  gap={1}
                  _hover={{
                    bg: "rgba(129, 126, 255, 0.2)",
                    borderColor: "rgba(129, 126, 255, 0.5)"
                  }}
                  _active={{
                    bg: "rgba(129, 126, 255, 0.3)"
                  }}
                  transition="all 0.2s ease"
                >
                  <Text fontSize="13px" color="white" fontWeight="500">
                    {contactInfo.phone}
                  </Text>
                  <Icon as={FaPhone} color="#817EFF" fontSize="10px" />
                </Box>
              </Flex>
            )}
            
            {contactInfo.website && (
              <Flex alignItems="center" gap={2}>
                <Icon as={FaGlobe} color="#817EFF" fontSize="12px" />
                <Box
                  as="a"
                  href={contactInfo.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  bg="rgba(129, 126, 255, 0.1)"
                  border="1px solid"
                  borderColor="rgba(129, 126, 255, 0.3)"
                  borderRadius="6px"
                  px={2}
                  py={1}
                  display="flex"
                  alignItems="center"
                  gap={1}
                  _hover={{
                    bg: "rgba(129, 126, 255, 0.2)",
                    borderColor: "rgba(129, 126, 255, 0.5)"
                  }}
                  _active={{
                    bg: "rgba(129, 126, 255, 0.3)"
                  }}
                  transition="all 0.2s ease"
                  textDecoration="none"
                >
                  <Text fontSize="13px" color="white" fontWeight="500">
                    Visit Website
                  </Text>
                  <Icon as={FaExternalLinkAlt} color="#817EFF" fontSize="10px" />
                </Box>
              </Flex>
            )}
          </Box>
        )}

        {/* Description */}
        {guide?.description && (
          <Box>
            <Box 
              fontSize="14px" 
              lineHeight="1.6" 
              color="gray.200"
              whiteSpace="pre-wrap"
              fontFamily="inherit"
            >
              <div dangerouslySetInnerHTML={{ __html: renderedDescription }} />
            </Box>
          </Box>
        )}
      </Box>
    </Flex>
  );

  // Regular drawer for non-modal contexts
  return (
    <Drawer placement="bottom" onClose={onClose} isOpen={isOpen} size="full">
      <DrawerOverlay />
      <DrawerContent 
        bg="#1A202C" 
        color="white" 
        {...bottomDrawerSize({ mobile: 80, desktop: 88 })}
        borderTopRadius="20px"
        display="flex"
        flexDirection="column"
        maxW={{
          base: "100%",
          md: guide?.images?.[0] && guide?.description ? "880px" : "640px",
        }}
        mx="auto"
        mb={{ md: 8 }}
        borderRadius={{ md: "24px" }}
      >
        <DrawerHeader px={{ base: 4, md: 6 }} py={4} borderBottom="1px solid" borderColor="rgba(255, 255, 255, 0.1)" flexShrink={0}>
          <Flex alignItems="center" justifyContent="space-between">
            <Flex alignItems="center" gap={3}>
              <Flex direction="column">
                <Text fontSize={{ base: "16px", md: "18px" }} fontWeight="700" lineHeight="1.2">
                  {guide?.title}
                </Text>
                {guide?.catalog?.name && (
                  <Badge
                    colorScheme="purple"
                    variant="subtle"
                    fontSize="10px"
                    px={2}
                    py={1}
                    mt={1}
                    alignSelf="flex-start"
                  >
                    {guide.catalog.name}
                  </Badge>
                )}
              </Flex>
            </Flex>
            <DrawerCloseButton pos="relative" top={0} right={0} />
          </Flex>
        </DrawerHeader>

        <DrawerBody px={0} pt={0} pb={6} overflowY="auto" flex={1} minH={0} overscrollBehavior="contain">
          {guideContent}
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
}; 